// The Stewards' city: the middle of the island, zoomed in. A hex is not one
// building - it is a block with PLOTS, and buildings take one or two of
// them. The city starts as three hexes in a triangle and annexes more as
// the Stewards need room (and the Frontier gets more to defend).
//
// Pure helpers over `civ.city = { hexes: [[q, r]], buildings: [...] }`
// where a building is { id, type, hex: [q, r], staffed, burned }.

import { neighbours, key as hexKey, hexDistance, siteAt, isLand } from './map';
import { modsOf } from './mods';

export const PLOTS_PER_HEX = 6;
export const START_CITY_HEXES = [[0, 0], [1, 0], [1, -1]];

// What can be built. `slots` = workers it takes in a day; `plots` = room.
export const BUILDINGS = {
  field: {
    name: 'Field', group: 'work', plots: 1, slots: 1, cost: { goods: 1 },
    text: 'A shift here grows 2 food, more with the right technologies. A mill adds one per field.',
  },
  workshop: {
    name: 'Workshop', group: 'work', plots: 1, slots: 1, cost: { goods: 1, coin: 1 },
    text: 'A shift here makes 2 goods.',
  },
  market: {
    name: 'Market', group: 'work', plots: 2, slots: 1, cost: { goods: 3 },
    text: 'A shift here sells 2 goods for 4 coin. Every market lets one more caravan go, and makes buying grain possible.',
  },
  // HOUSING (after playtest 11): the town only grows while it has homes.
  house: {
    name: 'House', group: 'homes', plots: 1, slots: 0, cost: { goods: 2 },
    text: 'Homes for 2 more people. The town only grows - by a family, or the Hall\'s Grow - while it has room.',
  },
  granary: {
    name: 'Granary', group: 'stores', plots: 2, slots: 0, cost: { goods: 2, coin: 1 },
    text: 'Keeps 6 more food from spoiling overnight.',
  },
  warehouse: {
    name: 'Warehouse', group: 'stores', plots: 2, slots: 0, cost: { goods: 2, coin: 1 }, needsTech: 'pottery',
    text: 'Keeps 6 more goods from spoiling overnight. Needs Pottery - ask the Scholars.',
  },
  mill: {
    name: 'Mill', group: 'work', plots: 2, slots: 0, cost: { goods: 3, coin: 2 }, needsTech: 'wheel',
    text: 'Every staffed field grows 1 more food. Needs The Wheel - ask the Scholars.',
  },
  // THE MACHINES (2026-10-15): one of each, built when the Scholars learn
  // how, and the only place the deep tiers' technologies are made.
  kiln: {
    name: 'Kiln', group: 'machines', plots: 2, slots: 0, cost: { goods: 3, coin: 1 }, helps: 'science', needsTech: 'kilns', unique: true,
    text: 'The Scholars fire two of the eight in it to make a glaze; tier 3 technologies are made from glazes. Needs Kilns - ask the Scholars.',
  },
  still: {
    name: 'Still', group: 'machines', plots: 2, slots: 0, cost: { goods: 4, coin: 2 }, helps: 'science', needsTech: 'distilling', unique: true,
    text: 'The Scholars run two glazes through it to make an essence; tier 4 technologies are made from essences. Needs Distilling - ask the Scholars.',
  },
  crucible: {
    name: 'Crucible', group: 'machines', plots: 2, slots: 0, cost: { goods: 5, coin: 3 }, helps: 'science', needsTech: 'assaying', unique: true,
    text: 'The Scholars melt two essences in it to make an alloy; tier 5 technologies are made from alloys. Needs Assaying - ask the Scholars.',
  },
  library: {
    name: 'Library', group: 'learning', plots: 2, slots: 0, cost: { goods: 3, coin: 2 }, helps: 'science', // was 2 + 1: paid back in 3 nights (docs/ECONOMY.md)
    text: 'The Scholars get one more experiment a day at the 1-worker price before it climbs, and Study yields one more. They will ask you for it.',
  },
};
export const BUILDING_ORDER = Object.keys(BUILDINGS);
// The Build menu's shelves, in order (after playtest 11, Matt: "a single
// 'build a building' button that pulled up a menu of buildings they have
// access to... look to Civilization"). Civ lists what a city can build by
// kind, keeps one-per-city buildings out once built, and shows what is
// still locked behind a technology - so does this.
export const BUILDING_GROUPS = [
  { id: 'work', label: 'Work' },
  { id: 'homes', label: 'Homes' },
  { id: 'stores', label: 'Stores' },
  { id: 'learning', label: 'Learning' },
  { id: 'machines', label: 'The Scholars\' machines' },
];

export const FOOD_CAP_BASE = 6;
export const GOODS_CAP_BASE = 4;
export const CAP_PER_STORE = 6;
// Priced in the Stewards' shifts: 2 is a person for the whole day. Taking in
// land should cost more than a day's work - Matt, 2026-09-18: "it seems like
// it should be harder to expand the city than that."
export const ANNEX_COST = { goods: 5, workers: 3 };
export const REPAIR_COST = { goods: 1, workers: 1 };

export const startCity = () => ({
  hexes: START_CITY_HEXES.map((h) => h.slice()),
  buildings: [
    { id: 'b1', type: 'field', hex: [0, 0], staffed: 0, burned: false },
    { id: 'b2', type: 'field', hex: [0, 0], staffed: 0, burned: false },
    { id: 'b3', type: 'market', hex: [1, 0], staffed: 0, burned: false },
    { id: 'b4', type: 'workshop', hex: [1, -1], staffed: 0, burned: false },
    // Homes for the founders and a little room to grow (Matt, 2026-10-15:
    // "the starting city [should] have homes for the starting population,
    // maybe with a bit of room to grow... this will fill up the 3 hexes of
    // the city faster and mean that the city has to take in new land more
    // often. Which I like.")
    { id: 'b5', type: 'house', hex: [1, -1], staffed: 0, burned: false },
    { id: 'b6', type: 'house', hex: [1, -1], staffed: 0, burned: false },
    { id: 'b7', type: 'house', hex: [0, 0], staffed: 0, burned: false },
  ],
  seq: 7,
});

export const cityOf = (civ) => civ?.city || startCity();
const sameHex = (a, b) => a[0] === b[0] && a[1] === b[1];

export const plotsUsed = (city, hex) => city.buildings
  .filter((b) => sameHex(b.hex, hex))
  .reduce((n, b) => n + (BUILDINGS[b.type]?.plots || 1), 0);
export const roomIn = (city, hex) => PLOTS_PER_HEX - plotsUsed(city, hex);
/** The first city hex with room for a building of this type, or null. */
export const hexWithRoom = (city, type) => city.hexes.find((h) => roomIn(city, h) >= (BUILDINGS[type]?.plots || 1)) || null;
/** Does the town know what it takes to build this? */
export const canBuildType = (civ, type) => Boolean(BUILDINGS[type]) && (!BUILDINGS[type].needsTech || (civ.techs || []).includes(BUILDINGS[type].needsTech))
  && !(BUILDINGS[type].unique && cityOf(civ).buildings.some((b) => b.type === type));
export const totalRoom = (city) => city.hexes.reduce((n, h) => n + roomIn(city, h), 0);

export const count = (civ, type) => cityOf(civ).buildings.filter((b) => b.type === type && !b.burned).length;
/** What keeps overnight: the base, the stores, and what the tree and the wonders add. */
export const foodCap = (civ) => FOOD_CAP_BASE + CAP_PER_STORE * count(civ, 'granary') + modsOf(civ).larder;
export const goodsCap = (civ) => GOODS_CAP_BASE + CAP_PER_STORE * count(civ, 'warehouse') + modsOf(civ).storehouse;
/** Shifts a building takes in a day. Coinage and Taxation give the market one more. */
export const slotsFor = (civ, type) => (BUILDINGS[type]?.slots || 0) + (type === 'market' ? Math.max(0, modsOf(civ).marketCap) : 0);

/** A machine standing (and not burned) in the city. */
export const machineStands = (civ, type) => cityOf(civ).buildings.some((b) => b.type === type && !b.burned);

/** Buildings a hand could still work today. */
export const workable = (civ) => cityOf(civ).buildings.filter((b) => !b.burned && b.staffed < slotsFor(civ, b.type));

/** Land next to the city that could be annexed: ring 1-2, not a site, not already in. */
export const annexable = (civ) => {
  const city = cityOf(civ);
  const have = new Set(city.hexes.map(([q, r]) => hexKey(q, r)));
  const out = new Map();
  city.hexes.forEach(([q, r]) => neighbours(q, r).forEach(([a, b]) => {
    const k = hexKey(a, b);
    if (have.has(k) || out.has(k)) return;
    if (hexDistance(a, b) > 2 || siteAt(a, b) >= 0 || !isLand(a, b, civ)) return;
    // Only ground somebody has looked at: the Frontier's scouting opens the city's room.
    if (civ.seen && !civ.seen.includes(k)) return;
    out.set(k, [a, b]);
  }));
  return [...out.values()];
};

/** The city hex nearest a point: where a raid lands. */
export const edgeHexToward = (civ, q, r) => {
  const city = cityOf(civ);
  return city.hexes.reduce((best, h) => {
    const d = hexDistance(h[0] - q, h[1] - r);
    return !best || d < best.d ? { h, d } : best;
  }, null)?.h || null;
};
